import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as MagnifyingGlass } from "../../assets/svgs/sf-symbols/magnifyingglass.svg";
import { ReactComponent as Battery } from "../../assets/svgs/sf-symbols/battery.100percent.svg";
import { ReactComponent as Wifi } from "../../assets/svgs/sf-symbols/wifi.svg";
import NotesApp from "./NotesApp";
import PhoneApp from "./PhoneApp";
import MessagesApp from "./MessagesApp";
import ChromeApp from "./ChromeApp";
import LockScreen from "./LockScreen";
import MobileSearch from "./MobileSearch";
import { notify } from "../Notification";
import useClock, { formatClock } from "../../hooks/useClock";
import { SignalIcon } from "../icons";
import { DOCK, PAGES } from "../../data/phone";
import { contact } from "../../data/search";

const openUrl = (href) => window.open(href, "_blank", "noopener");
const hint = () =>
  notify("Only Notes, Phone, Messages and Chrome open on this iPhone. Tap Notes in the dock to see my resume.");
const canFullscreen = typeof document !== "undefined" && !!document.documentElement.requestFullscreen;
// A mouse drag turns the page once it covers this much of it, or when flicked faster than FLICK.
const TURN = 0.18;
const FLICK = 0.4; // px per ms
const ROWS = 6; // rows of apps on a page when the screen is tall enough

// The apps in pages of `rows` rows. With the full six rows that's the phone's own pages; on a
// shorter screen (Safari's toolbars take room) the same apps, in order, over more pages.
const paginate = (rows) => {
  if (rows >= ROWS) return PAGES;
  const all = PAGES.flat();
  const perPage = rows * 4;
  return Array.from({ length: Math.ceil(all.length / perPage) }, (_, i) => all.slice(i * perPage, (i + 1) * perPage));
};

const AppIcon = ({ app, onOpen }) => (
  <button
    type="button"
    className="ios-app-icon"
    onClick={() => onOpen(app)}
    aria-label={app.badge ? `${app.name}, ${app.badge} notifications` : app.name}
  >
    <img src={app.icon} alt="" draggable={false} />
    {app.badge && (
      <span className="ios-badge" aria-hidden="true">
        {app.badge}
      </span>
    )}
  </button>
);

// The iPhone Home Screen, laid out like Sylvester's: two pages of apps, the Search button (which
// shows the page dots while you swipe between pages) and the dock, where Notes opens the resume.
// It starts locked, behind the Lock Screen.
const MobileHome = () => {
  // "locked", then "unlocking" while the Lock Screen slides away, then "open".
  const [lock, setLock] = useState("locked");
  const [unlockProgress, setUnlockProgress] = useState(0);
  // The open app, if any: { id: "notes", anchor } (where in the resume to land), or { id: "phone" },
  // { id: "messages" } or { id: "chrome" }.
  const [app, setApp] = useState(null);
  const [searching, setSearching] = useState(false);
  const [isFull, setIsFull] = useState(false);
  const [page, setPage] = useState(0);
  const [paging, setPaging] = useState(false);
  const now = useClock(10000);
  const searchInput = useRef(null);
  const swipe = useRef(null);
  const pagingTimer = useRef(null);
  const pagesRef = useRef(null);
  const drag = useRef(null);
  const [rows, setRows] = useState(ROWS);
  const pages = useMemo(() => paginate(rows), [rows]);

  // How many rows of icons fit above the Search button, measured from the layout (offset sizes,
  // which ignore the zoom the Home Screen waits in while locked), and again on every resize.
  useEffect(() => {
    const el = pagesRef.current;
    const measure = () => {
      const grid = el.querySelector(".ios-page-grid");
      const icon = grid && grid.querySelector(".ios-app-icon");
      if (!icon) return;
      const style = getComputedStyle(grid);
      const gap = parseFloat(style.rowGap) || 0;
      const room = el.clientHeight - parseFloat(style.paddingTop) - 4;
      setRows(Math.max(1, Math.min(ROWS, Math.floor((room + gap) / (icon.offsetHeight + gap)))));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onChange = () => setIsFull(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      clearTimeout(pagingTimer.current);
    };
  }, []);

  const unlock = () => {
    if (lock !== "locked") return;
    setLock("unlocking");
    setUnlockProgress(1);
    setTimeout(() => setLock("open"), 450);
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else document.documentElement.requestFullscreen().catch(() => {});
  };

  const openApp = (icon) => {
    if (icon.action === "resume") setApp({ id: "notes" });
    else if (["phone", "messages", "chrome"].includes(icon.action)) setApp({ id: icon.action });
    else if (icon.action === "email") openUrl(contact("Email").href);
    else if (icon.action === "linkedin") openUrl(contact("LinkedIn").href);
    else hint();
  };

  // Focus the field within the tap or swipe itself: iOS Safari only raises the keyboard then.
  const openSearch = () => {
    setSearching(true);
    if (searchInput.current) searchInput.current.focus({ preventScroll: true });
  };

  // Touch swipes scroll the pages natively. A mouse can drag them too: the pages follow the
  // pointer (with snapping off), then glide to the nearest page, or the next one if the drag
  // went far or fast enough. A drag that ends on an icon doesn't also open it.
  const onPagesPointerDown = (e) => {
    if (e.pointerType !== "mouse" || e.button !== 0) return;
    drag.current = { id: e.pointerId, x: e.clientX, t: e.timeStamp, left: pagesRef.current.scrollLeft, moving: false };
  };

  const onPagesPointerMove = (e) => {
    const d = drag.current;
    if (!d || e.pointerId !== d.id) return;
    const el = pagesRef.current;
    const dx = e.clientX - d.x;
    if (!d.moving && Math.abs(dx) > 6) {
      d.moving = true;
      el.setPointerCapture(d.id);
      el.classList.add("is-dragging");
    }
    if (d.moving) el.scrollLeft = d.left - dx;
  };

  const onPagesPointerUp = (e) => {
    const d = drag.current;
    drag.current = null;
    if (!d || !d.moving) return;
    const el = pagesRef.current;
    const width = el.clientWidth;
    const dx = e.clientX - d.x;
    const speed = dx / Math.max(1, e.timeStamp - d.t);
    const from = Math.round(d.left / width);
    let to = from;
    if (dx < -width * TURN || speed < -FLICK) to = from + 1;
    else if (dx > width * TURN || speed > FLICK) to = from - 1;
    to = Math.max(0, Math.min(pages.length - 1, to));
    el.scrollTo({ left: to * width, behavior: "smooth" });
    // Snapping comes back once the glide has landed on the page.
    setTimeout(() => el.classList.remove("is-dragging"), 450);
    const swallow = (ev) => {
      ev.stopPropagation();
      ev.preventDefault();
    };
    el.addEventListener("click", swallow, { capture: true, once: true });
    setTimeout(() => el.removeEventListener("click", swallow, { capture: true }), 0);
  };

  const onPagesScroll = (e) => {
    const el = e.currentTarget;
    setPage(Math.round(el.scrollLeft / el.clientWidth));
    setPaging(true);
    clearTimeout(pagingTimer.current);
    pagingTimer.current = setTimeout(() => setPaging(false), 700);
  };

  // What Search can open: the apps (Notes is Search's own), and FullScreen where it works.
  const searchApps = [
    ...[...PAGES.flat(), ...DOCK]
      .filter((a) => a.action !== "resume")
      .map((a) => ({ id: a.id, title: a.name, icon: a.icon, run: () => openApp(a) })),
    ...(canFullscreen
      ? [{ id: "app-fullscreen", title: "FullScreen", keywords: "full screen", run: toggleFullscreen }]
      : []),
  ];

  return (
    <div
      className={`ios no-select ${searching ? "is-searching" : ""} ${lock === "locked" ? "is-locked" : ""} ${
        lock === "locked" && unlockProgress > 0 ? "is-peeking" : ""
      }`}
      style={{ "--unlock": unlockProgress }}
    >
      {/* Swiping down on the Home Screen opens Search, as on iOS. */}
      <div
        className="ios-home"
        onPointerDown={(e) => (swipe.current = { x: e.clientX, y: e.clientY })}
        onPointerUp={(e) => {
          const start = swipe.current;
          swipe.current = null;
          if (start && e.clientY - start.y > 60 && Math.abs(e.clientX - start.x) < 60) openSearch();
        }}
        onPointerCancel={() => (swipe.current = null)}
      >
        {isFull && (
          <div className="ios-status">
            <span className="tabular-nums">{formatClock(now)}</span>
            <span className="flex items-center gap-[6px]">
              <SignalIcon className="w-[18px] h-[12px]" />
              <Wifi className="w-[17px] h-[12px]" />
              <Battery className="w-[27px] h-[13px]" />
            </span>
          </div>
        )}

        <div
          ref={pagesRef}
          className="ios-pages"
          onScroll={onPagesScroll}
          onPointerDown={onPagesPointerDown}
          onPointerMove={onPagesPointerMove}
          onPointerUp={onPagesPointerUp}
          onPointerCancel={onPagesPointerUp}
        >
          {pages.map((apps, i) => (
            <div key={i} className="ios-page-grid" aria-label={`Page ${i + 1} of ${pages.length}`}>
              {apps.map((app) => (
                <AppIcon key={app.id} app={app} onOpen={openApp} />
              ))}
            </div>
          ))}
        </div>

        <div className="ios-bottom">
          <button
            type="button"
            className={`ios-search ${paging ? "is-paging" : ""}`}
            onClick={openSearch}
            aria-label="Search"
          >
            <span className="ios-search-label">
              <MagnifyingGlass className="ios-search-icon" aria-hidden="true" />
              Search
            </span>
            <span className="ios-page-dots" aria-hidden="true">
              {pages.map((_, i) => (
                <i key={i} className={i === page ? "is-current" : ""} />
              ))}
            </span>
          </button>
          <nav className="ios-dock" aria-label="Dock">
            {DOCK.map((app) => (
              <AppIcon key={app.id} app={app} onOpen={openApp} />
            ))}
          </nav>
        </div>
      </div>

      <MobileSearch
        ref={searchInput}
        open={searching}
        onClose={() => setSearching(false)}
        onOpenResume={(anchor) => setApp({ id: "notes", anchor })}
        homeApps={searchApps}
      />

      {app && app.id === "notes" && <NotesApp now={now} anchor={app.anchor} onClose={() => setApp(null)} />}
      {app && app.id === "phone" && <PhoneApp now={now} onClose={() => setApp(null)} />}
      {app && app.id === "messages" && <MessagesApp now={now} onClose={() => setApp(null)} />}
      {app && app.id === "chrome" && <ChromeApp onClose={() => setApp(null)} />}

      {lock !== "open" && (
        <LockScreen now={now} leaving={lock === "unlocking"} onProgress={setUnlockProgress} onUnlock={unlock} />
      )}
    </div>
  );
};

export default MobileHome;
